import { useEffect, useState } from 'react';
import {
  ArrowDownOutlined, ArrowUpOutlined, BulbOutlined, DeleteOutlined, ExperimentOutlined, LockOutlined,
  PlusOutlined, RobotOutlined, SendOutlined,
} from '@ant-design/icons';

import { Badge, Button, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { KIEU_TIEU_CHI } from '../constants';
import { maDangDung, sinhMa, tyTrongNhom, tyTrongTieuChi } from '../nghiep-vu/ty-trong';
import { useBatDauGoiY, useCanTrongSo, useLuotGoiY, useLuuNhap } from '../hooks/useThamDinh';
import type { BanNhap, BoTieuChiChiTiet, KetQuaCanTrongSo, NhomTieuChi, TieuChi } from '../types';
import { HopCanTrongSo } from './HopCanTrongSo';
import { HopChayThu } from './HopChayThu';
import { HopGoiYTieuChi } from './HopGoiYTieuChi';

/** Bản nháp của một bộ chưa soạn gì: ngưỡng mặc định theo đặc tả mục 5.1. */
const NHAP_RONG: BanNhap = { nguong_dat: 80, nguong_dat_dieu_kien: 60, nhom: [] };

/** Màu của từng nhóm trên thanh tỉ trọng — nhóm phân loại của `Badge`, không mang nghĩa trạng thái. */
const TONG_THANH = ['bg-primary-500', 'bg-teal-500', 'bg-violet-500', 'bg-orange-500'] as const;

export interface TrinhSoanBoProps {
  bo: BoTieuChiChiTiet;
  /** Mã tiêu chí đang mở ở cột phải. */
  dangChon?: string;
  onChonTieuChi: (ma: string | undefined) => void;
  /**
   * Trao cho view một hàm sửa tiêu chí đang chọn, để cột phải ghi thẳng vào bản nháp ở đây.
   * Bản nháp phải nằm một chỗ — tách nó ra hai state là mở đường cho hai cây lệch nhau.
   */
  onDangKySua: (f: ((sua: (tc: TieuChi) => void) => void) | undefined) => void;
  duocSua: boolean;
  onPhatHanh: () => void;
  /** Mở màn chi tiết của lượt chạy thử; view quyết điều hướng, trình soạn không biết đường dẫn. */
  onChayThu: (phienId: string) => void;
}

/**
 * Cột giữa của Thư viện tiêu chí: soạn cây tiêu chí của một bộ, theo giao diện v3.
 *
 * Soạn **tại chỗ trong bộ nhớ** rồi lưu cả cây một lượt (`PUT /ban-nhap`), không lưu từng ô: cây nằm trong
 * một khối JSONB (đặc tả §19.1), nên mỗi lần sửa một trọng số mà gọi API là ghi lại cả cây — và hai người
 * soạn cùng lúc sẽ ghi đè nhau liên tục thay vì va vào khoá lạc quan một lần.
 *
 * `ty_trong` và `loi_phat_hanh` lấy từ **phản hồi của backend**, không tính lại ở đây: chúng là cùng phép
 * tính mà lượt chấm thật sẽ dùng (`cham-diem/cham.ts`). Hệ quả cố ý: sửa trọng số xong thì % chưa đổi ngay,
 * phải Lưu rồi backend trả lại — thà chậm một nhịp còn hơn hiện một con số khác với con số dùng để chấm.
 */
export function TrinhSoanBo({ bo, dangChon, onChonTieuChi, onDangKySua, duocSua, onPhatHanh,
  onChayThu }: TrinhSoanBoProps) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const luu = useLuuNhap();
  const can = useCanTrongSo();
  const batDauGoiY = useBatDauGoiY();

  const [ban, setBan] = useState<BanNhap>(bo.ban_nhap ?? NHAP_RONG);
  const [ban_dau, setBanDau] = useState<string>(JSON.stringify(bo.ban_nhap ?? NHAP_RONG));
  const [tenMoi, setTenMoi] = useState('');
  /** Đề xuất đang chờ người soạn quyết; `undefined` = không có lượt nào đang xem. */
  const [deXuat, setDeXuat] = useState<KetQuaCanTrongSo | undefined>();
  /** Hộp gợi ý có đang mở; `luotGoiYId` rỗng = mới mở, chưa bấm. */
  const [moChayThu, setMoChayThu] = useState(false);
  const [moGoiY, setMoGoiY] = useState(false);
  const [luotGoiYId, setLuotGoiYId] = useState<string | undefined>();

  // Đổi bộ hoặc backend trả bản mới thì nạp lại; mốc `updated_at` là khoá lạc quan của lần lưu sau.
  useEffect(() => {
    const n = bo.ban_nhap ?? NHAP_RONG;
    setBan(n);
    setBanDau(JSON.stringify(n));
    setTenMoi('');
    setDeXuat(undefined);
    setMoChayThu(false);
    setMoGoiY(false);
    setLuotGoiYId(undefined);
    luu.reset();
    can.reset();
    batDauGoiY.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bo.id, bo.cap_nhat_luc]);

  /*
   * Đăng ký hàm sửa cho cột phải, theo tiêu chí ĐANG CHỌN. Gói trong một hàm trả hàm vì `setState` coi
   * tham số hàm là bản cập nhật lười, không phải giá trị cần lưu.
   */
  useEffect(() => {
    if (!dangChon) {
      onDangKySua(undefined);
      return;
    }
    onDangKySua(() => (sua: (tc: TieuChi) => void) => {
      doi((b) => {
        for (const n of b.nhom) {
          const tc = n.tieu_chi.find((x) => x.ma === dangChon);
          if (tc) {
            sua(tc);
            break;
          }
        }
        return b;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dangChon]);

  const coThayDoi = JSON.stringify(ban) !== ban_dau;
  const loiPhatHanh = bo.loi_phat_hanh ?? [];
  const soTieuChi = ban.nhom.reduce((n, x) => n + x.tieu_chi.length, 0);
  // Poll lượt gợi ý; `enabled` tắt khi chưa có lượt nên hook này không gọi gì lúc hộp vừa mở
  const luotGoiY = useLuotGoiY(bo.id, luotGoiYId);

  function doi(f: (b: BanNhap) => BanNhap) {
    setBan((cu) => f(structuredClone(cu)));
  }

  function themNhom() {
    const ten = tenMoi.trim();
    if (!ten) return;
    doi((b) => {
      b.nhom.push({ ma: sinhMa(ten, maDangDung(b)), ten, trong_so: 1, tieu_chi: [] });
      return b;
    });
    setTenMoi('');
  }

  function themTieuChi(iNhom: number, ten: string) {
    const sach = ten.trim();
    if (!sach) return;
    doi((b) => {
      b.nhom[iNhom]?.tieu_chi.push({
        ma: sinhMa(sach, maDangDung(b)),
        ten: sach,
        kieu: 'co_khong',
        trong_so: 1,
        muc_nghiem_trong: 'trung_binh',
      });
      return b;
    });
  }

  /** Đổi chỗ hai phần tử; `huong` là -1 (lên) hoặc 1 (xuống). */
  function doiCho<T>(ds: T[], i: number, huong: -1 | 1) {
    const j = i + huong;
    if (j < 0 || j >= ds.length) return;
    [ds[i], ds[j]] = [ds[j]!, ds[i]!];
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
      {/*
        * Thanh công cụ: tên bộ một hàng, nhóm nút một hàng — **không** xếp cạnh nhau.
        *
        * Cột này hẹp (một phần ba màn ở bố cục ba cột), và bốn nút mang nhãn tiếng Việt dài
        * ("AI cân trọng số", "Phát hành v1"). Để chung một hàng thì phần tên bị `flex-1` co xuống
        * dưới bề rộng một từ: tên bộ thành "Ti…", badge vỡ thành ba dòng chữ xếp dọc. Tách hàng thì
        * tên luôn đọc được, và nhóm nút tự xuống dòng theo chỗ còn lại.
        */}
      <div className="space-y-2 border-b border-default p-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-content-primary" title={bo.ten}>{bo.ten}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge variant={bo.phien_ban_hien_tai === null ? 'neutral' : 'success'}>
              {bo.phien_ban_hien_tai === null
                ? t('thamDinh.soan.chuaPhatHanh')
                : t('thamDinh.soan.dangApDung', { so: soVN(bo.phien_ban_hien_tai, locale) })}
            </Badge>
            {bo.nguoi_cap_nhat && (
              <span className="text-2xs text-content-muted">
                {t('thamDinh.soan.suaGanNhat', { luc: ngayGioVN(bo.updated_at, locale) })}
              </span>
            )}
          </div>
        </div>
        {duocSua && (
          <div className="flex flex-wrap items-center gap-2">
            {/*
              * AI cân trọng số: ẩn khi bản nháp chưa có tiêu chí — backend trả 409 cho bộ rỗng, và một nút
              * bấm xong để nhận lỗi là một nút không nên hiện.
              */}
            <Button
              variant="outline"
              size="sm"
              icon={<BulbOutlined />}
              onClick={() => { setMoGoiY(true); setLuotGoiYId(undefined); batDauGoiY.reset(); }}
            >
              {t('thamDinh.soan.goiY')}
            </Button>
            {soTieuChi > 0 && (
              <Button
                variant="outline"
                size="sm"
                icon={<ExperimentOutlined />}
                onClick={() => setMoChayThu(true)}
              >
                {t('thamDinh.soan.chayThu')}
              </Button>
            )}
            {soTieuChi > 0 && (
              <Button
                variant="outline"
                size="sm"
                icon={<RobotOutlined />}
                loading={can.isPending}
                onClick={() => can.mutate(bo.id, { onSuccess: setDeXuat })}
              >
                {t('thamDinh.soan.canTrongSo')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              icon={<SendOutlined />}
              disabled={loiPhatHanh.length > 0}
              title={loiPhatHanh.length > 0 ? loiPhatHanh.join(' ') : undefined}
              onClick={onPhatHanh}
            >
              {t('thamDinh.soan.phatHanh', { so: soVN((bo.phien_ban_hien_tai ?? 0) + 1, locale) })}
            </Button>
          </div>
        )}
      </div>

      {can.error && (
        <p className="border-b border-default bg-danger-50 p-3 text-sm text-danger-700">{dichLoi(can.error)}</p>
      )}

      {/*
        * Áp đề xuất vào bản nháp TRONG BỘ NHỚ, không gọi API lưu: người soạn còn phải xem lại cây rồi bấm
        * Lưu như mọi lần sửa khác. Áp rồi lưu luôn là bỏ qua đúng bước mà `can-trong-so` sinh ra để giữ.
        */}
      {/*
        * Gợi ý tiêu chí: **THAY** cả cây tiêu chí trong bộ nhớ (khác cân trọng số chỉ đổi trọng số), và
        * cũng chưa lưu — người soạn còn phải xem lại rồi bấm Lưu.
        */}
      <HopChayThu
        open={moChayThu}
        onClose={() => setMoChayThu(false)}
        boId={bo.id}
        boTen={bo.ten}
        onXong={onChayThu}
      />

      {moGoiY && (
        <HopGoiYTieuChi
          luot={luotGoiY.data}
          dangGui={batDauGoiY.isPending}
          loiGui={batDauGoiY.error}
          onBatDau={(mo_ta, tep) => batDauGoiY.mutate({ id: bo.id, mo_ta, tep },
            { onSuccess: (l) => setLuotGoiYId(l.id) })}
          onApDung={() => {
            const kq = luotGoiY.data?.ket_qua;
            if (kq) setBan((cu) => ({ ...cu, nhom: kq.nhom }));
            setMoGoiY(false);
            setLuotGoiYId(undefined);
          }}
          onDong={() => { setMoGoiY(false); setLuotGoiYId(undefined); }}
        />
      )}

      {deXuat && (
        <HopCanTrongSo
          kq={deXuat}
          dangApDung={false}
          onApDung={() => { setBan(deXuat.ban_nhap); setDeXuat(undefined); }}
          onBo={() => setDeXuat(undefined)}
        />
      )}

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {/* Ngưỡng kết luận */}
        <section aria-label={t('thamDinh.soan.nguong')} className="rounded-lg border border-default bg-subtle p-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-content-primary">{t('thamDinh.soan.nguong')}</span>
            <label className="flex items-center gap-2 text-sm">
              <Badge variant="success">{t('thamDinh.ketLuan.dat')}</Badge>
              <span className="text-content-muted">≥</span>
              <Input
                className="w-20"
                type="number"
                min={0}
                max={100}
                disabled={!duocSua}
                aria-label={t('thamDinh.soan.nguongDat')}
                value={ban.nguong_dat}
                onChange={(e) => doi((b) => ({ ...b, nguong_dat: Number(e.target.value) }))}
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Badge variant="warning">{t('thamDinh.ketLuan.dat_co_dieu_kien')}</Badge>
              <span className="text-content-muted">≥</span>
              <Input
                className="w-20"
                type="number"
                min={0}
                max={100}
                disabled={!duocSua}
                aria-label={t('thamDinh.soan.nguongDatDieuKien')}
                value={ban.nguong_dat_dieu_kien}
                onChange={(e) => doi((b) => ({ ...b, nguong_dat_dieu_kien: Number(e.target.value) }))}
              />
            </label>
          </div>
          <p className="mt-2 text-2xs text-content-muted">{t('thamDinh.soan.nguongMo')}</p>
        </section>

        {/* Thanh tỉ trọng */}
        {ban.nhom.length > 0 && (
          <div>
            <div className="flex h-2 overflow-hidden rounded-full bg-subtle" role="img" aria-label={t('thamDinh.soan.thanhTyTrong')}>
              {ban.nhom.map((n, i) => {
                const pc = tyTrongNhom(n, bo.ty_trong);
                return pc > 0 ? (
                  <div key={n.ma} className={cn('h-full', TONG_THANH[i % TONG_THANH.length])} style={{ width: `${pc}%` }} />
                ) : null;
              })}
            </div>
            <p className="mt-1 text-2xs text-content-muted">{t('thamDinh.soan.thanhTyTrong')}</p>
          </div>
        )}

        {/* Nhóm và tiêu chí */}
        {ban.nhom.map((nhom, iNhom) => (
          <KhoiNhom
            key={nhom.ma}
            nhom={nhom}
            tyTrong={bo.ty_trong}
            duocSua={duocSua}
            dangChon={dangChon}
            onChonTieuChi={onChonTieuChi}
            onDoiTrongSo={(v) => doi((b) => { b.nhom[iNhom]!.trong_so = v; return b; })}
            onChuyen={(h) => doi((b) => { doiCho(b.nhom, iNhom, h); return b; })}
            onXoa={() => doi((b) => { b.nhom.splice(iNhom, 1); return b; })}
            onThemTieuChi={(ten) => themTieuChi(iNhom, ten)}
            onDoiTieuChi={(iTc, f) => doi((b) => { f(b.nhom[iNhom]!.tieu_chi[iTc]!); return b; })}
            onChuyenTieuChi={(iTc, h) => doi((b) => { doiCho(b.nhom[iNhom]!.tieu_chi, iTc, h); return b; })}
            onXoaTieuChi={(iTc) => doi((b) => { b.nhom[iNhom]!.tieu_chi.splice(iTc, 1); return b; })}
          />
        ))}

        {duocSua && (
          <div className="flex gap-2">
            <Input
              className="flex-1"
              placeholder={t('thamDinh.soan.tenNhomMoi')}
              aria-label={t('thamDinh.soan.tenNhomMoi')}
              value={tenMoi}
              onChange={(e) => setTenMoi(e.target.value)}
              onPressEnter={themNhom}
            />
            <Button variant="outline" size="md" icon={<PlusOutlined />} disabled={!tenMoi.trim()} onClick={themNhom}>
              {t('thamDinh.soan.themNhom')}
            </Button>
          </div>
        )}

        {/* Còn thiếu gì để phát hành — hiện TRONG LÚC soạn, không đợi bấm (đặc tả mục 13.3). */}
        {loiPhatHanh.length > 0 && (
          <section aria-label={t('thamDinh.soan.chuaPhatHanhDuoc')} className="rounded-lg border border-warning-200 bg-warning-50 p-3">
            <p className="text-sm font-medium text-warning-800">{t('thamDinh.soan.chuaPhatHanhDuoc')}</p>
            <ul className="mt-1 list-disc space-y-0.5 ps-5 text-sm text-warning-700">
              {loiPhatHanh.map((l) => <li key={l}>{l}</li>)}
            </ul>
          </section>
        )}
      </div>

      {/* Chân: trạng thái lưu */}
      {duocSua && (
        <div className="flex flex-wrap items-center gap-2 border-t border-default p-3">
          {luu.error ? (
            <p className="min-w-0 flex-1 text-sm text-danger-700">{dichLoi(luu.error)}</p>
          ) : (
            <p className="min-w-0 flex-1 text-sm text-content-muted">
              {coThayDoi ? t('thamDinh.soan.chuaLuu') : t('thamDinh.soan.daLuu')}
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={!coThayDoi || luu.isPending}
            onClick={() => { setBan(JSON.parse(ban_dau) as BanNhap); luu.reset(); }}
          >
            {t('thamDinh.soan.hoanTac')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={luu.isPending}
            disabled={!coThayDoi}
            onClick={() => luu.mutate({ id: bo.id, du: { ban_nhap: ban, cap_nhat_luc: bo.cap_nhat_luc } })}
          >
            {t('chung.luu')}
          </Button>
        </div>
      )}
    </div>
  );
}

interface KhoiNhomProps {
  nhom: NhomTieuChi;
  tyTrong: Record<string, number> | undefined;
  duocSua: boolean;
  dangChon?: string;
  onChonTieuChi: (ma: string | undefined) => void;
  onDoiTrongSo: (v: number) => void;
  onChuyen: (huong: -1 | 1) => void;
  onXoa: () => void;
  onThemTieuChi: (ten: string) => void;
  onDoiTieuChi: (i: number, f: (tc: TieuChi) => void) => void;
  onChuyenTieuChi: (i: number, huong: -1 | 1) => void;
  onXoaTieuChi: (i: number) => void;
}

function KhoiNhom({
  nhom, tyTrong, duocSua, dangChon, onChonTieuChi, onDoiTrongSo, onChuyen, onXoa,
  onThemTieuChi, onDoiTieuChi, onChuyenTieuChi, onXoaTieuChi,
}: KhoiNhomProps) {
  const { t, locale } = useT();
  const [tenMoi, setTenMoi] = useState('');

  function them() {
    onThemTieuChi(tenMoi);
    setTenMoi('');
  }

  return (
    <section aria-label={nhom.ten} className="rounded-lg border border-default">
      <div className="flex items-center gap-2 border-b border-default bg-subtle px-3 py-2">
        <span className="min-w-0 flex-1 truncate font-medium text-content-primary">{nhom.ten}</span>
        <Input
          className="w-20"
          type="number"
          min={0}
          disabled={!duocSua}
          aria-label={t('thamDinh.soan.trongSoNhom', { ten: nhom.ten })}
          value={nhom.trong_so}
          onChange={(e) => onDoiTrongSo(Number(e.target.value))}
        />
        <span className="w-14 text-end text-sm font-medium text-primary-600">
          {soVN(tyTrongNhom(nhom, tyTrong), locale)}%
        </span>
        {duocSua && <NutSapXep onLen={() => onChuyen(-1)} onXuong={() => onChuyen(1)} onXoa={onXoa} ten={nhom.ten} />}
      </div>

      <ul className="divide-y divide-default">
        {nhom.tieu_chi.map((tc, i) => (
          <li key={tc.ma}>
            <div className={cn('flex items-center gap-2 px-3 py-2', tc.ma === dangChon && 'bg-primary-50')}>
              {tc.loai_tru && <LockOutlined className="shrink-0 text-warning-600" title={t('thamDinh.soan.batBuoc')} />}
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-start text-sm text-content-primary hover:underline"
                onClick={() => onChonTieuChi(tc.ma === dangChon ? undefined : tc.ma)}
              >
                {tc.ten}
              </button>
              <span className="hidden shrink-0 text-2xs text-content-muted sm:inline">{t(KIEU_TIEU_CHI[tc.kieu])}</span>
              <Input
                className="w-20"
                type="number"
                min={0}
                disabled={!duocSua}
                aria-label={t('thamDinh.soan.trongSoTieuChi', { ten: tc.ten })}
                value={tc.trong_so}
                onChange={(e) => onDoiTieuChi(i, (x) => { x.trong_so = Number(e.target.value); })}
              />
              <span className="w-14 text-end text-sm text-content-secondary">
                {soVN(tyTrongTieuChi(tc.ma, tyTrong), locale)}%
              </span>
              {duocSua && (
                <NutSapXep
                  onLen={() => onChuyenTieuChi(i, -1)}
                  onXuong={() => onChuyenTieuChi(i, 1)}
                  onXoa={() => onXoaTieuChi(i)}
                  ten={tc.ten}
                />
              )}
            </div>
          </li>
        ))}
      </ul>

      {duocSua && (
        <div className="flex gap-2 border-t border-default p-2">
          <Input
            className="flex-1"
            placeholder={t('thamDinh.soan.tenTieuChiMoi')}
            aria-label={t('thamDinh.soan.tenTieuChiMoi')}
            value={tenMoi}
            onChange={(e) => setTenMoi(e.target.value)}
            onPressEnter={them}
          />
          <Button variant="ghost" size="sm" icon={<PlusOutlined />} disabled={!tenMoi.trim()} onClick={them}>
            {t('thamDinh.soan.themTieuChi')}
          </Button>
        </div>
      )}
    </section>
  );
}

/** Ba nút sắp xếp và xoá, lặp ở cả hàng nhóm lẫn hàng tiêu chí. */
function NutSapXep({ onLen, onXuong, onXoa, ten }: { onLen: () => void; onXuong: () => void; onXoa: () => void; ten: string }) {
  const { t } = useT();
  return (
    <span className="flex shrink-0 gap-0.5">
      <Button variant="ghost" size="sm" aria-label={t('thamDinh.soan.chuyenLen', { ten })} onClick={onLen}>
        <ArrowUpOutlined />
      </Button>
      <Button variant="ghost" size="sm" aria-label={t('thamDinh.soan.chuyenXuong', { ten })} onClick={onXuong}>
        <ArrowDownOutlined />
      </Button>
      <Button variant="ghost" size="sm" aria-label={t('thamDinh.soan.xoaMuc', { ten })} onClick={onXoa}>
        <DeleteOutlined />
      </Button>
    </span>
  );
}
